import { SlotItem } from '../slotItem';
import store from '@/store';
import { ElementMenuItem } from '../elementMenuItem';
import Element, { ElementType, SpecyficType } from '../element';
import { SlotElement } from '../slotElement';
import { ShaderSlot } from '../shaders/others/shader_slot';
import { Vector2, MeshBuilder, AbstractMesh } from '@babylonjs/core';
import { positionManager } from '../positionManager';
import { placementManager } from '../placementManager';

class RegionManager {

    private get canPickRegionMesh(): boolean {
        return this.selectedElement && store.getters.scene.tasks === 0;
    }

    private get canSelectSlot(): boolean {
        return this.selectedElement !== null && this.indicator !== null;
    }

    private get selectedElement(): ElementMenuItem {
        return store.getters.scene.selectedElementMenuItem;
    }

    private get body(): Element {
        return this.scenePreview.elements
            .find((x: Element) => x.type === ElementType.BODY)!;
    }

    private scenePreview!: any;
    private indicator: SlotElement | null = null;
    private shader = new ShaderSlot();
    private material: any = null;

    public initialize(scenePreview: any) {
        this.scenePreview = scenePreview;
        this.subscribeStore();
    }

    public getSlot(): SlotItem | null {
        if (!this.canPickRegionMesh) { return null; }
        const pickedRegionMesh = this.getPickedRegionMesh();
        if (!pickedRegionMesh) {
            this.removeIndicator();
            return null;
        }
        const region = this.getPickedRegionFromMesh(pickedRegionMesh);
        if (!region) {
            this.removeIndicator();
            return null;
        }
        const slot = this.toSlot(region);
        this.removeIndicator();
        this.placeIndicator(slot);
        return slot;
    }

    public onPointerUp() {
        const slot = this.getSlot();
        if (this.canSelectSlot) {
            store.commit('selectSlotItem', slot);
        }
    }

    private getPickedRegionFromMesh(mesh: AbstractMesh): SlotElement {
        return this.scenePreview.regionElements
            .find((x: SlotElement) => x.meshes[0] === mesh);
    }

    private getPickedRegionMesh(): AbstractMesh | null {
        const scene = this.scenePreview.scene;
        const pickInfo = scene.pick(scene.pointerX, scene.pointerY);
        return pickInfo.pickedMesh;
    }

    private subscribeStore() {
        store.subscribe((mutation, state) => {
            if (mutation.type === 'selectElementMenuItem') {
                this.createRegions();
            }
            if (mutation.type === 'clearSceneState') {
                this.removeIndicator();
            }
        });
    }

    private removeIndicator() {
        if (!this.indicator) { return; }
        this.indicator.remove();
        this.indicator = null;
    }

    private placeIndicator(slot: SlotItem) {
        if (this.indicator) { return; }
        const meshParams = this.getIndicatorParams(slot);

        const material = this.material || this.scenePreview.getMaterial(this.shader, '');
        this.material = material;
        const mesh = MeshBuilder.CreateBox(
            'slot',
            meshParams,
            this.scenePreview.scene,
        );
        mesh.isPickable = false;
        mesh.material = material;
        const indicator = new SlotElement(
            slot.position,
            slot.shape,
            [mesh],
        );
        positionManager.position(indicator);
        this.correctIndicatorPosition(indicator);
        this.indicator = indicator;
    }

    private correctIndicatorPosition(indicator: SlotElement) {
        if (this.selectedElement.type === ElementType.FRONT) {
            indicator.center.z -= this.body.depth / 2;
        }
        if (this.selectedElement.specyficType === SpecyficType.HANGER) {
            indicator.center.y += positionManager.cellHeight * 2 / 3 + 5.5;
        }
        if (this.selectedElement.specyficType === SpecyficType.SHELF) {
            indicator.center.y -= 0.9;
        }
    }

    private getIndicatorParams(slot: SlotItem): any {
        let height = slot.shape.y * positionManager.cellHeight - 1;
        let width = slot.shape.x * positionManager.cellWidth - 1;
        let depth = positionManager.cellDepth - 1;
        if (this.selectedElement.specyficType === SpecyficType.SHELF) {
            height = 2;
        }
        if (this.selectedElement.type === ElementType.FRONT ||
            this.selectedElement.specyficType === SpecyficType.HANGER) {
            depth = 2;
        }
        if (this.selectedElement.specyficType === SpecyficType.HANGER) {
            height = 3;
            depth = 2;
        }
        return { height, width, depth };
    }

    private createRegions() {
        this.clearRegions();
        const body = this.body;
        const selectedElementShapeY = this.selectedElement.shape.y ? this.selectedElement.shape.y : 0.5;
        const xElements = body.shape.x - (this.selectedElement.shape.x - 1);
        const yElements = this.getYElements(body, selectedElementShapeY);
        const height = body.height / yElements;
        const width = body.width / xElements;
        const depth = positionManager.cellDepth + 4;
        const regionParams = { height, width, depth };
        for (let i = 0; i < xElements; i++) {
            for (let j = 1; j <= this.getMaxJ(yElements); j++) {
                if (this.selectedElement.specyficType === SpecyficType.HANGER &&
                    j === 1) {
                    continue;
                }
                this.createRegion(body, selectedElementShapeY, regionParams, xElements, yElements, i, j);
            }
        }
    }

    private getYElements(body: Element, selectedElementShapeY: number): number {
        if (this.selectedElement.specyficType === SpecyficType.SHELF) {
            return 2 * body.shape.y - 1;
        }
        if (this.selectedElement.specyficType === SpecyficType.HANGER) {
            return body.shape.y;
        }
        return body.shape.y - (selectedElementShapeY - 1);
    }

    private getMaxJ(yElements: number): number {
        if (this.selectedElement.specyficType === SpecyficType.SHELF) {
            return yElements + 1;
        }
        return yElements;

    }

    private createRegion(
        body: Element,
        selectedElementShapeY: number,
        regionParams: any,
        xElements: number,
        yElements: number,
        i: number,
        j: number,
    ) {
        const shape = this.getRegionShape(body, xElements, yElements);
        const position = this.getRegionPosition(body, xElements, yElements, i, j);
        const region = MeshBuilder.CreateBox(
            'slot',
            regionParams,
            this.scenePreview.scene,
        );
        const regionElement = new SlotElement(position, shape, [region]);
        regionElement.isVisible = false;
        regionElement.meshes[0].isPickable = true;
        const slot = this.toSlot(regionElement);
        if (!placementManager.canPlace(
            slot.position,
            slot.shape,
            this.selectedElement.type,
            this.selectedElement.specyficType,
        )) {
            regionElement.remove();
            return;
        }
        positionManager.position(regionElement);
        this.scenePreview.regionElements.push(regionElement);
    }

    private getRegionShape(body: Element, xElements: number, yElements: number): Vector2 {
        return new Vector2(body.shape.x / xElements, body.shape.y / yElements);
    }

    private getRegionPosition(
        body: Element,
        xElements: number,
        yElements: number,
        i: number,
        j: number,
    ): Vector2 {
        return new Vector2(
            (i + 1) * (body.shape.x + 1) / (xElements + 1),
            this.selectedElement.specyficType !== SpecyficType.SHELF ?
                j * (body.shape.y + 1) / (yElements + 1) :
                j === 1 ? j : j / 2,
        );
    }

    private toSlot(region: SlotElement): SlotItem {
        const position = region.position.clone();
        position.x = region.position.x > this.body.shape.x / 2 ?
            Math.floor(region.position.x * 2) / 2 :
            Math.ceil(region.position.x * 2) / 2;
        position.y = region.position.y > this.body.shape.y / 2 ?
            Math.floor(region.position.y * 2) / 2 :
            Math.ceil(region.position.y * 2) / 2;

        if (this.selectedElement.specyficType === SpecyficType.HANGER) {
            position.y = region.position.y - 0.5;
        }

        return new SlotItem(
            '',
            position,
            this.selectedElement.shape,
        );
    }

    private clearRegions() {
        this.scenePreview.regionElements.forEach((x: SlotElement) => {
            x.remove();
        });
        this.scenePreview.regionElements = [];
    }
}

export const regionManager = new RegionManager();
